"use client"

import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
} from "@apollo/client"
import { onError } from "@apollo/client/link/error"
import React, { useMemo } from "react"
import { createAzureInsightsLink } from "./azureInsightsLink"
import {
  createErrorContext,
  createIsraeliTimestamp,
  logGraphQLErrors,
  logNetworkError,
} from "./errorLink"
import generatedIntrospection from "./introspection-result.json"

// For client-side operations, use the client-facing GraphQL URL
const GRAPHQL_URL = process.env.GRAPHQL_URL || ""

function makeClient() {
  const httpLink = new HttpLink({
    uri: GRAPHQL_URL,
  })

  const azureInsightsLink = createAzureInsightsLink()

  const errorLink = onError(({ graphQLErrors, operation, networkError }) => {
    try {
      const errorContext = createErrorContext(operation, GRAPHQL_URL)

      if (graphQLErrors) {
        logGraphQLErrors(graphQLErrors, errorContext)
      }

      if (networkError) {
        logNetworkError(networkError, errorContext)
      }
    } catch (errorHandlingError) {
      // Fallback error logging if our error handling fails
      console.error("=== ERROR HANDLER FAILED ===")
      console.error("Error handling error:", errorHandlingError)
      console.error("Original GraphQL errors:", graphQLErrors)
      console.error("Original network error:", networkError)
      console.error("Operation:", operation)
      console.error("GraphQL URL:", GRAPHQL_URL)
      console.error("Timestamp:", createIsraeliTimestamp())
      console.error("==============================")
    }
  })

  return new ApolloClient({
    cache: new InMemoryCache({
      possibleTypes: generatedIntrospection.possibleTypes,
    }),
    link: azureInsightsLink.concat(errorLink).concat(httpLink),
  })
}

interface ApolloWrapperProps {
  children: React.ReactNode
}

export function ApolloWrapper({ children }: ApolloWrapperProps) {
  // CRITICAL FIX: Use useMemo to create client only once per component lifecycle
  // Previously, makeClient() was called on every render, causing severe memory leaks
  const client = useMemo(() => makeClient(), [])

  return <ApolloProvider client={client}>{children}</ApolloProvider>
}
